/**
 * PayrollPage — Dashboard de Nómina Quincenal
 *
 * Muestra:
 *   • Período activo (OPEN) con total acumulado
 *   • KPI cards: total nómina, trabajadores, desglose por tipo
 *   • Tabla de entries del período activo
 *   • Historial de períodos cerrados
 */

import { useFieldErrors } from '../hooks/useFieldErrors';
import { useMemo, useState } from 'react';
import {
  Text,
  Spinner,
  Badge,
  Button,
  Field,
  Input,
  Dropdown,
  Combobox,
  Option,
  makeStyles,
  tokens,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPayrollSummary, fetchPayrollPeriods,
  openPayrollPeriod, closePayrollPeriod, addPayrollEntry,
  type OpenPayrollPeriodInput, type AddPayrollEntryInput,
} from '../api/payroll.api';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { useEntityNames } from '../hooks/useEntityNames';
import { fetchAssignees } from '../api/dashboard.api';
import { looksLikeId } from '../utils/entity-names';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { DateInput } from '../components/shared/DateInput';
import { KpiCard } from '../components/shared/KpiCard';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { PageTitle } from '../components/shared/PageTitle';
import { SubTabs } from '../components/shared/SubTabs';

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: '24px' },
  kpiRow: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '24px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
    overflowX:       'auto',
  },
  table: {
    width:           '100%',
    borderCollapse: 'collapse' as const,
    fontSize:        '13px',
    minWidth:        '560px',
  },
  th: {
    textAlign:    'left' as const,
    padding:      '8px 0',
    color:        tokens.colorNeutralForeground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize:     '12px',
    fontWeight:   '600',
  },
  td: {
    padding:      '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusBadge: { fontSize: '11px' },
  emptyMsg: {
    padding:   '24px',
    textAlign: 'center' as const,
    color:     tokens.colorNeutralForeground3,
  },
  periodHeader: {
    display:    'flex',
    alignItems: 'center',
    gap:        '12px',
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function payTypeBadge(payType: string, label: string) {
  const color: Record<string, 'brand' | 'success' | 'warning'> = {
    JORNAL:   'brand',
    DESTAJO:  'success',
    CONTRATO: 'warning',
  };
  return (
    <Badge appearance="tint" color={color[payType] ?? 'brand'} size="small">
      {label}
    </Badge>
  );
}

// ─── OpenPeriodDialog ─────────────────────────────────────────────────────────

function OpenPeriodDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t  = useT();
  const p  = t.payroll;
  const qc = useQueryClient();

  const [label, setLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [costBasis, setCostBasis] = useState<'TOTAL_PRODUCTION' | 'SELLABLE_PRODUCTION'>('TOTAL_PRODUCTION');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => { setLabel(''); setStartDate(''); setEndDate(''); setCostBasis('TOTAL_PRODUCTION'); setError(''); fe.clear(); };

  const mut = useMutation({
    mutationFn: () => {
      const input: OpenPayrollPeriodInput = { label: label.trim(), startDate, endDate, costBasis };
      return openPayrollPeriod(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payrollSummary'] });
      qc.invalidateQueries({ queryKey: ['payrollPeriods'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? p.openPeriodError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      label: !label.trim() && p.labelRequired,
      startDate: !startDate && p.datesRequired,
      endDate: !endDate && p.datesRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '440px' }}>
        <DialogBody>
          <DialogTitle>{p.openPeriodTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Field label={p.fieldLabel} {...fe.props('label')}>
                <Input value={label} onChange={(_, d) => { setLabel(d.value); fe.clear('label'); }} placeholder={p.fieldLabelPlaceholder} disabled={mut.isPending} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={p.fieldStartDate} {...fe.props('startDate')}>
                  <DateInput value={startDate} onValueChange={(v) => { setStartDate(v); fe.clear('startDate'); }} disabled={mut.isPending} />
                </Field>
                <Field label={p.fieldEndDate} {...fe.props('endDate')}>
                  <DateInput value={endDate} onValueChange={(v) => { setEndDate(v); fe.clear('endDate'); }} disabled={mut.isPending} />
                </Field>
              </div>
              <Field label={p.fieldCostBasis}>
                <Dropdown
                  selectedOptions={[costBasis]}
                  value={costBasis === 'TOTAL_PRODUCTION' ? p.costBasisTotal : p.costBasisSellable}
                  onOptionSelect={(_, d) => setCostBasis(d.optionValue as typeof costBasis)}
                  disabled={mut.isPending}
                >
                  <Option value="TOTAL_PRODUCTION">{p.costBasisTotal}</Option>
                  <Option value="SELLABLE_PRODUCTION">{p.costBasisSellable}</Option>
                </Dropdown>
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {p.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── ClosePeriodDialog ────────────────────────────────────────────────────────

function ClosePeriodDialog({ periodId, onClose }: { periodId: string | null; onClose: () => void }) {
  const t  = useT();
  const p  = t.payroll;
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: () => closePayrollPeriod(periodId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payrollSummary'] });
      qc.invalidateQueries({ queryKey: ['payrollPeriods'] });
      onClose();
    },
  });

  return (
    <Dialog open={!!periodId} onOpenChange={(_, d) => !d.open && onClose()} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{p.closePeriodBtn}</DialogTitle>
          <DialogContent><Text>{p.closePeriodConfirm}</Text></DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {p.closePeriodBtn}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── AddEntryDialog ───────────────────────────────────────────────────────────

/** Trabajador: lista buscable (usuarios activos + nombres ya pagados) que admite nombre nuevo. */
export function WorkerCombobox({ value, onChange, options, disabled, placeholder, freeText }: {
  value: string; onChange: (v: string) => void; options: string[];
  disabled?: boolean; placeholder: string; freeText: string;
}) {
  const q = value.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.toLowerCase().includes(q)) : options;
  const isNew = !!q && !options.some(o => o.toLowerCase() === q);
  return (
    <Combobox
      freeform
      placeholder={placeholder}
      value={value}
      selectedOptions={[value]}
      onChange={(e) => onChange((e.target as HTMLInputElement).value)}
      onOptionSelect={(_, d) => { if (d.optionText) onChange(d.optionText); }}
      disabled={disabled}
    >
      {filtered.map(o => <Option key={o} value={o}>{o}</Option>)}
      {isNew && <Option key="__new" value="__new" disabled text={value}>{freeText}: {value.trim()}</Option>}
    </Combobox>
  );
}

function AddEntryDialog({ open, onClose, periodId }: { open: boolean; onClose: () => void; periodId: string }) {
  const t  = useT();
  const p  = t.payroll;
  const qc = useQueryClient();
  const [loteId, setLoteId] = useState('');

  const { lotes } = useRanchoLotes(open, { value: loteId, reset: () => setLoteId('') });
  const [workerName, setWorkerName] = useState('');
  const { data: assignees } = useQuery({
    queryKey: ['assignees'], queryFn: fetchAssignees, enabled: open, retry: false,
  });
  const { data: paid } = useQuery({
    queryKey: ['payrollSummary', null], queryFn: () => fetchPayrollSummary(null), enabled: open,
  });
  const workerOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const a of assignees ?? []) if (a.kind === 'USER' && a.label.trim() && !/^[+\d]+$/.test(a.label.trim())) names.set(a.label.trim().toLowerCase(), a.label.trim());
    for (const e of paid?.entries ?? []) if (e.workerName?.trim()) names.set(e.workerName.trim().toLowerCase(), e.workerName.trim());
    return [...names.values()].sort((x, y) => x.localeCompare(y));
  }, [assignees, paid]);
  const [workerPhone, setWorkerPhone] = useState('');
  const [payType, setPayType] = useState<'JORNAL' | 'DESTAJO' | 'CONTRATO'>('JORNAL');
  const [activity, setActivity] = useState('');
  const [days, setDays] = useState('');
  const [units, setUnits] = useState('');
  const [unitLabel, setUnitLabel] = useState('');
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    fe.clear();
    setLoteId(''); setWorkerName(''); setWorkerPhone(''); setPayType('JORNAL');
    setActivity(''); setDays(''); setUnits(''); setUnitLabel(''); setRate(''); setNotes(''); setError('');
  };

  const mut = useMutation({
    mutationFn: () => {
      const input: AddPayrollEntryInput = {
        loteId, workerName: workerName.trim(), workerPhone: workerPhone.trim() || undefined,
        payType, activity: activity.trim(),
        days: days ? parseFloat(days) : undefined,
        units: units ? parseFloat(units) : undefined,
        unitLabel: unitLabel.trim() || undefined,
        rate: parseFloat(rate) || 0,
        notes: notes.trim() || undefined,
      };
      return addPayrollEntry(periodId, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payrollSummary'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? p.addEntryError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      loteId: !loteId && p.loteRequired,
      workerName: !workerName.trim() && p.workerRequired,
      activity: !activity.trim() && p.activityRequired,
      rate: (!rate || parseFloat(rate) <= 0) && p.rateRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="payroll"
              voiceExample={t.telegramHelp.payrollVoice}
              voiceDesc={t.telegramHelp.payrollDesc}
            />
          }>{p.addEntryTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={p.fieldWorkerName} {...fe.props('workerName')}>
                  <WorkerCombobox value={workerName} onChange={(v) => { setWorkerName(v); fe.clear('workerName'); }} options={workerOptions} disabled={mut.isPending}
                    placeholder={p.workerPlaceholder} freeText={p.workerFreeText} />
                </Field>
                <Field label={p.fieldWorkerPhone}>
                  <Input value={workerPhone} onChange={(_, d) => setWorkerPhone(d.value)} disabled={mut.isPending} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={p.fieldLoteId} {...fe.props('loteId')}>
                  <SearchableSelect
                    placeholder={p.lotePlaceholder}
                    options={lotes.map(l => ({ id: l.loteId, label: l.name }))}
                    value={loteId}
                    onChange={(id) => { setLoteId(id); fe.clear('loteId'); }}
                    disabled={mut.isPending}
                  />
                </Field>
                <Field label={p.fieldPayType}>
                  <Dropdown
                    selectedOptions={[payType]}
                    value={payType}
                    onOptionSelect={(_, d) => setPayType(d.optionValue as typeof payType)}
                    disabled={mut.isPending}
                  >
                    <Option value="JORNAL">{p.payTypeJORNAL}</Option>
                    <Option value="DESTAJO">{p.payTypeDESTAJO}</Option>
                    <Option value="CONTRATO">{p.payTypeCONTRATO}</Option>
                  </Dropdown>
                </Field>
              </div>

              <Field label={p.fieldActivity} {...fe.props('activity')}>
                <Input value={activity} onChange={(_, d) => { setActivity(d.value); fe.clear('activity'); }} disabled={mut.isPending} />
              </Field>

              {payType === 'JORNAL' && (
                <Field label={p.fieldDays}>
                  <NumberInput value={days} onValueChange={(v) => setDays(v)} disabled={mut.isPending} />
                </Field>
              )}
              {payType === 'DESTAJO' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                  <Field label={p.fieldUnits}>
                    <NumberInput value={units} onValueChange={(v) => setUnits(v)} disabled={mut.isPending} />
                  </Field>
                  <Field label={p.fieldUnitLabel}>
                    <Input value={unitLabel} onChange={(_, d) => setUnitLabel(d.value)} disabled={mut.isPending} />
                  </Field>
                </div>
              )}

              <Field label={p.fieldRate} {...fe.props('rate')}>
                <NumberInput value={rate} onValueChange={(v) => { setRate(v); fe.clear('rate'); }} disabled={mut.isPending} />
              </Field>

              <Field label={p.fieldNotes}>
                <Input value={notes} onChange={(_, d) => setNotes(d.value)} disabled={mut.isPending} />
              </Field>

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {p.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function PayrollPage() {
  const s = useStyles();
  const t = useT();
  const p = t.payroll;
  const payTypeLabel = (x: string) => x === 'JORNAL' ? p.payTypeJORNAL : x === 'DESTAJO' ? p.payTypeDESTAJO : p.payTypeCONTRATO;
  const { user } = useAuthStore();
  const isAdmin = user?.roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN') ?? false;
  const { formatCurrency } = useLocaleSettings();
  const { loteName: rawLoteName } = useEntityNames();
  const loteName = (id: string) => {
    const n = rawLoteName(id);
    return looksLikeId(id) && n === `#${id.slice(0, 8)}` ? p.unknownLote : n;
  };
  const [tab, setTab] = useState<string>('activo');
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<string | null>(null);

  const { activeRanchoId } = useRanchoStore();
  const {
    data: summary,
    isLoading: loadingSummary,
  } = useQuery({
    queryKey: ['payrollSummary', activeRanchoId],
    queryFn:  () => fetchPayrollSummary(activeRanchoId),
  });

  const {
    data: periods,
    isLoading: loadingPeriods,
  } = useQuery({
    queryKey:  ['payrollPeriods'],
    queryFn:   fetchPayrollPeriods,
    enabled:   tab === 'historial',
  });

  if (loadingSummary) {
    return <Spinner size="small" label={p.loading} />;
  }

  const open    = summary?.openPeriod;
  const entries = summary?.entries ?? [];

  const chartData = (summary?.byLote ?? []).map(l => ({
    loteId: loteName(l.loteId),
    monto:  l.amount,
  }));

  return (
    <div className={s.root}>

      {/* ── Encabezado ─────────────────────────────────────────────────────── */}
      <div className={s.periodHeader} style={{ justifyContent: 'space-between' }}>
        <div className={s.periodHeader}>
          <PageTitle>{p.title}</PageTitle>
          {open ? (
            <Badge appearance="tint" color="warning" className={s.statusBadge}>
              {p.openPeriod}: {open.label}
            </Badge>
          ) : (
            <Badge appearance="tint" color="subtle" className={s.statusBadge}>
              {p.noActivePeriod}
            </Badge>
          )}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {open ? (
              <>
                <Button onClick={() => setAddEntryOpen(true)}>{p.addEntryBtn}</Button>
                <Button appearance="secondary" onClick={() => setCloseTarget(open.periodId)}>{p.closePeriodBtn}</Button>
              </>
            ) : (
              <Button appearance="primary" onClick={() => setOpenDialogOpen(true)}>{p.openPeriodBtn}</Button>
            )}
          </div>
        )}
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className={s.kpiRow}>
        <KpiCard
          label={p.kpiTotal}
          value={formatCurrency(summary?.totalAmount ?? 0)}
          sub={open ? `${open.startDate} – ${open.endDate}` : '—'}
        />
        <KpiCard
          label={p.kpiWorkers}
          value={String(summary?.workerCount ?? 0)}
          sub={p.kpiWorkersSub}
        />
        <KpiCard
          label={p.kpiJornal}
          value={formatCurrency(summary?.byPayType?.JORNAL ?? 0)}
          sub={p.kpiJornalSub}
        />
        <KpiCard
          label={p.kpiDestajo}
          value={formatCurrency(summary?.byPayType?.DESTAJO ?? 0)}
          sub={p.kpiDestajoCub}
        />
        <KpiCard
          label={p.kpiContrato}
          value={formatCurrency(summary?.byPayType?.CONTRATO ?? 0)}
          sub={p.kpiContratoSub}
        />
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <SubTabs selected={tab} onSelect={setTab} tabs={[{ value: 'activo', label: p.tabActive }, { value: 'graficas', label: p.tabByLot }, { value: 'historial', label: p.tabHistory }]} />

      {/* ── Tab: Período Activo ─────────────────────────────────────────────── */}
      {tab === 'activo' && (
        <div className={s.card}>
          <Text weight="semibold" size={400}>
            {open ? `${p.recordsLabel} — ${open.label}` : p.noActivePeriodOpen}
          </Text>

          {entries.length === 0 ? (
            <div className={s.emptyMsg}>
              <Text>{p.noEntries}</Text>
              <br />
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {p.noEntriesSub}
              </Text>
            </div>
          ) : (
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{p.colWorker}</th>
                  <th className={s.th}>{p.colLot}</th>
                  <th className={s.th}>{p.colActivity}</th>
                  <th className={s.th}>{p.colType}</th>
                  <th className={s.th}>{p.colDetail}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{p.colTotal}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => {
                  const detalle =
                    e.payType === 'JORNAL'   ? `${e.days ?? 0} ${p.jornalDays} × $${e.rate}` :
                    e.payType === 'DESTAJO'  ? `${e.units ?? 0} ${e.unitLabel ?? 'u'} × $${e.rate}` :
                    p.contratoLabel;

                  return (
                    <tr key={e.entryId}>
                      <td className={s.td}>{e.workerName}</td>
                      <td className={s.td}>{loteName(e.loteId)}</td>
                      <td className={s.td}>{e.activity}</td>
                      <td className={s.td}>{payTypeBadge(e.payType, payTypeLabel(e.payType))}</td>
                      <td className={s.td} style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>
                        {detalle}
                      </td>
                      <td className={s.td} style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(e.totalPay)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Tab: Por Lote ──────────────────────────────────────────────────── */}
      {tab === 'graficas' && (
        <div className={s.grid}>
          <div className={s.card}>
            <Text weight="semibold" size={400}>{p.chartTitle}</Text>
            {chartData.length === 0 ? (
              <div className={s.emptyMsg}><Text>{p.noData}</Text></div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="loteId" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="monto" name={p.chartLegend} fill={tokens.colorBrandBackground} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className={s.card}>
            <Text weight="semibold" size={400}>{p.distTitle}</Text>
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{p.distColType}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{p.distColAmount}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>%</th>
                </tr>
              </thead>
              <tbody>
                {(['JORNAL', 'DESTAJO', 'CONTRATO'] as const).map(type => {
                  const amount = summary?.byPayType?.[type] ?? 0;
                  const pct    = (summary?.totalAmount ?? 0) > 0
                    ? ((amount / summary!.totalAmount) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <tr key={type}>
                      <td className={s.td}>{payTypeBadge(type, payTypeLabel(type))}</td>
                      <td className={s.td} style={{ textAlign: 'right' }}>{formatCurrency(amount)}</td>
                      <td className={s.td} style={{ textAlign: 'right', color: tokens.colorNeutralForeground3 }}>
                        {pct}%
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td className={s.td} style={{ fontWeight: 600 }}>{p.distTotal}</td>
                  <td className={s.td} style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(summary?.totalAmount ?? 0)}
                  </td>
                  <td className={s.td} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Historial ─────────────────────────────────────────────────── */}
      {tab === 'historial' && (
        <div className={s.card}>
          <Text weight="semibold" size={400}>{p.historyTitle}</Text>
          {loadingPeriods ? (
            <Spinner size="tiny" label={p.historyLoading} />
          ) : !periods || periods.length === 0 ? (
            <div className={s.emptyMsg}><Text>{p.historyEmpty}</Text></div>
          ) : (
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{p.histColPeriod}</th>
                  <th className={s.th}>{p.histColDates}</th>
                  <th className={s.th}>{p.histColStatus}</th>
                  <th className={s.th}>{p.histColCostBasis}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{p.histColTotal}</th>
                </tr>
              </thead>
              <tbody>
                {periods.map(pp => (
                  <tr key={pp.periodId}>
                    <td className={s.td}>{pp.label}</td>
                    <td className={s.td} style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                      {pp.startDate} → {pp.endDate}
                    </td>
                    <td className={s.td}>
                      <Badge
                        appearance="tint"
                        color={pp.status === 'OPEN' ? 'warning' : 'success'}
                        size="small"
                      >
                        {pp.status === 'OPEN' ? p.statusOpen : p.statusClosed}
                      </Badge>
                    </td>
                    <td className={s.td} style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                      {pp.costBasis === 'TOTAL_PRODUCTION' ? p.costBasisTotal : p.costBasisSellable}
                    </td>
                    <td className={s.td} style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(pp.totalAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <OpenPeriodDialog open={openDialogOpen} onClose={() => setOpenDialogOpen(false)} />
      {open && <AddEntryDialog open={addEntryOpen} onClose={() => setAddEntryOpen(false)} periodId={open.periodId} />}
      <ClosePeriodDialog periodId={closeTarget} onClose={() => setCloseTarget(null)} />
    </div>
  );
}
